import { detectLocationType } from '@/src/utils'

/**
 * Retrieves category autocomplete suggestions from the API.
 * @param {object} query - The query parameters for the autocomplete request.
 * @return {Promise<{ body: object[]; total: number; spellSuggestion: string }>} - A Promise that resolves to the API response data.
 */
const getCategoryAutocompleteSearch = async (
  query: object
): Promise<{ body: object[]; total: number; spellSuggestion: string }> => {
  try {
    const response = await fetch(`/api/autocomplete/category`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(query),
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error during fetch: ', error)
    throw error
  }
}

/**
 * Retrieves full name autocomplete suggestions from the API.
 * @param {object} query - The query parameters for the autocomplete request.
 * @return {Promise<{ body: object[]; total: number }>} - A Promise that resolves to the API response data.
 */
const getFullNameAutocompleteSearch = async (
  query: object
): Promise<{ body: object[]; total: number }> => {
  try {
    const response = await fetch(`/api/autocomplete/full_name`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(query),
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error during fetch: ', error)
    throw error
  }
}

/**
 * Helper function to perform API requests for location autocomplete.
 * @param {object} queryItem - The query parameters for the location autocomplete request.
 * @return {Promise<{ body: object[]; total: number }>} - A Promise that resolves to the API response data.
 */
const fetchLocationAutocomplete = async (
  queryItem: object
): Promise<{ body: object[]; total: number }> => {
  try {
    const response = await fetch(`/api/location`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(queryItem),
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error during fetch: ', error)
    throw error
  }
}

/**
 * Retrieves autocomplete suggestions from the API.
 * @param {object} query - The query parameters for the autocomplete request.
 * @return {Promise<{ body: object[]; total: number; spellSuggestion: string }>} - A Promise that resolves to an array of autocomplete suggestions.
 */
export const autocompleteOpenSearch = async (query: {
  key: string
  value: string
}): Promise<{ body: object[]; total?: number; spellSuggestion?: string }> => {
  if (!query || !query.key || !query.value) {
    throw new Error('Missing or invalid query for autocomplete search')
  }

  const queryItem = {
    searchField: query.key === 'full_name' ? 'full_name' : 'category',
    searchValue: query.value.toLowerCase(),
  }

  if (queryItem.searchField === 'full_name') {
    return getFullNameAutocompleteSearch(queryItem)
  } else {
    return getCategoryAutocompleteSearch(queryItem)
  }
}

/**
 * Retrieves location autocomplete suggestions from the API.
 * @param {string} location - The location keyword for the autocomplete search.
 * @return {Promise<{ body: object[]; total: number }> } - A Promise that resolves to an array of location autocomplete suggestions.
 */
export const autocompleteLocationOpenSearch = async (
  location: string
): Promise<{ body: object[]; total: number }> => {
  if (!location) {
    throw new Error('Missing location')
  }

  const queryItem = {
    searchField: detectLocationType(location),
    searchValue: location.toLowerCase(),
  }

  return fetchLocationAutocomplete(queryItem)
}
